import { api } from '@dataroom/shared';

/**
 * Affiliate referral, browser side (ADR-0042). A visitor who arrives through an
 * affiliate's link carries `?ref=CODE` (straight to this app) or
 * `?ref=CODE&click=ID` (via the marketing site, which already recorded the
 * click). The NEWEST click id is kept in this origin's localStorage for 90 days
 * and sent with the free-trial signup — "last click wins" is simply that each
 * new referral visit overwrites it. Only the opaque click id is stored; the
 * server decides whether it still counts.
 *
 * Storage can be blocked or wiped (private windows, cleared site data): every
 * access is wrapped, and a missing value just means "no referral".
 */

export const REFERRAL_STORAGE_KEY = 'cv_affiliate_click';
export const REFERRAL_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CODE = /^[A-Za-z0-9]{6,32}$/;

interface StoredClick {
  clickId: string;
  at: number;
}

export function storeClick(clickId: string, now: number = Date.now()): void {
  try {
    const value: StoredClick = { clickId, at: now };
    window.localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* storage unavailable: no referral is remembered */
  }
}

/** The stored click id if it is still inside the 90-day window, else null. */
export function readStoredClick(now: number = Date.now()): string | null {
  try {
    const raw = window.localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredClick>;
    if (typeof parsed.clickId !== 'string' || !UUID.test(parsed.clickId)) return null;
    if (typeof parsed.at !== 'number' || now - parsed.at > REFERRAL_WINDOW_MS) {
      window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
      return null;
    }
    return parsed.clickId;
  } catch {
    return null;
  }
}

export function clearStoredClick(): void {
  try {
    window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Reads `?click=` / `?ref=` from the page URL and remembers the newest click.
 * A `click` id (already recorded by the marketing site) is stored as is; a bare
 * `ref` code is recorded as a click first. A failed or unknown code changes
 * nothing.
 */
export async function captureReferral(
  search: string,
  record: (code: string) => Promise<{ clickId: string }> = (code) =>
    api.post<{ clickId: string }>(`/affiliate-clicks/${encodeURIComponent(code)}`),
): Promise<void> {
  const params = new URLSearchParams(search);
  const click = params.get('click');
  if (click && UUID.test(click)) {
    storeClick(click);
    return;
  }
  const ref = params.get('ref');
  if (!ref || !CODE.test(ref)) return;
  try {
    const res = await record(ref);
    if (UUID.test(res.clickId)) storeClick(res.clickId);
  } catch {
    /* unknown or suspended code: no referral */
  }
}
